import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #duplicatePriceObserver;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#setupDuplicatePriceSync();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#duplicatePriceObserver?.disconnect();
    this.#duplicatePriceObserver = undefined;
  }

  /** Re-run after the price container node is replaced so the duplicate-price observer targets the new DOM. */
  refreshDuplicatePriceSync() {
    this.#setupDuplicatePriceSync();
  }

  /**
   * Keeps extra product-price blocks (e.g. below variant picker) in sync with the first block
   * when discount apps mutate only the primary price in the DOM.
   */
  #setupDuplicatePriceSync() {
    const section = this.closest('.shopify-section');
    const productId = this.dataset.productId;
    if (!section || !productId) return;

    const peers = [...section.querySelectorAll(`product-price[data-product-id="${productId}"]`)];
    if (peers.length < 2) return;

    const primary = peers[0];
    if (this !== primary) {
      this.#syncPriceContainerFrom(primary);
      return;
    }

    const container = primary.querySelector('[ref="priceContainer"]');
    if (!container) return;

    for (const peer of peers.slice(1)) {
      this.#syncPriceContainerFrom(primary, peer);
    }

    this.#duplicatePriceObserver?.disconnect();
    this.#duplicatePriceObserver = new MutationObserver(() => {
      for (const peer of peers.slice(1)) {
        this.#syncPriceContainerFrom(primary, peer);
      }
    });
    this.#duplicatePriceObserver.observe(container, { childList: true, subtree: true, characterData: true });
  }

  /**
   * @param {HTMLElement} primary
   * @param {HTMLElement} [peer]
   */
  #syncPriceContainerFrom(primary, peer = this) {
    if (peer === primary) return;
    const source = primary.querySelector('[ref="priceContainer"]');
    const target = peer.querySelector('[ref="priceContainer"]');
    if (!source || !target) return;
    if (target.innerHTML !== source.innerHTML) {
      target.innerHTML = source.innerHTML;
    }
  }

  /**
   * @param {Document} htmlDoc
   * @returns {Element | null}
   */
  #findPriceContainerInResponse(htmlDoc) {
    const blockId = this.dataset.blockId;
    if (blockId) {
      const scoped = htmlDoc.querySelector(`product-price[data-block-id="${blockId}"] [ref="priceContainer"]`);
      if (scoped) return scoped;
    }
    return htmlDoc.querySelector('product-price [ref="priceContainer"]');
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    const { productId, newProduct, html } = event.detail.data;

    if (newProduct) {
      this.dataset.productId = newProduct.id;
    } else if (productId !== undefined && productId !== '' && productId !== this.dataset.productId) {
      return;
    }

    const newPrice = this.#findPriceContainerInResponse(html);
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    const section = this.closest('.shopify-section');
    const pid = this.dataset.productId;
    if (section && pid) {
      const primary = section.querySelector(`product-price[data-product-id="${pid}"]`);
      if (primary && 'refreshDuplicatePriceSync' in primary) {
        /** @type {{ refreshDuplicatePriceSync: () => void }} */ (primary).refreshDuplicatePriceSync();
      }
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
